import { useEffect, useState } from 'react';

export enum DeviceType {
  MOBILE = 'mobile',
  TABLET = 'tablet',
  DESKTOP = 'desktop'
}

export interface DeviceInfo {
  deviceType: DeviceType;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isTouchDevice: boolean;
  screenSize: {
    width: number;
    height: number;
  };
}

/**
 * Hook to determine the current device type and capabilities
 */
export const useDeviceType = (): DeviceInfo => {
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>(() => {
    const width = typeof window !== 'undefined' ? window.innerWidth : 1024;
    const height = typeof window !== 'undefined' ? window.innerHeight : 768;
    const isTouchDevice = typeof window !== 'undefined' && 'ontouchstart' in window;
    
    let deviceType = DeviceType.DESKTOP;
    if (width < 768) {
      deviceType = DeviceType.MOBILE;
    } else if (width >= 768 && width < 1024) {
      deviceType = DeviceType.TABLET;
    }

    return {
      deviceType,
      isMobile: deviceType === DeviceType.MOBILE,
      isTablet: deviceType === DeviceType.TABLET,
      isDesktop: deviceType === DeviceType.DESKTOP,
      isTouchDevice,
      screenSize: { width, height }
    };
  });

  useEffect(() => {
    // Function to determine device type based on window width
    const updateDeviceInfo = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const isTouchDevice = 'ontouchstart' in window;
      
      let deviceType = DeviceType.DESKTOP;
      if (width < 768) {
        deviceType = DeviceType.MOBILE;
      } else if (width >= 768 && width < 1024) {
        deviceType = DeviceType.TABLET;
      }

      setDeviceInfo({
        deviceType,
        isMobile: deviceType === DeviceType.MOBILE,
        isTablet: deviceType === DeviceType.TABLET,
        isDesktop: deviceType === DeviceType.DESKTOP,
        isTouchDevice,
        screenSize: { width, height }
      });
    };

    // Set initial device type
    updateDeviceInfo();

    // Add event listener for window resize
    window.addEventListener('resize', updateDeviceInfo);

    // Cleanup event listener
    return () => window.removeEventListener('resize', updateDeviceInfo);
  }, []);

  return deviceInfo;
};
